<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterReadings',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_paging = computed(() => false);
  const b_7315092382398562_columns = computed(() => [
    {
      name: 'locked',
      type: 'lock',
    },
    {
      name: 'inactive',
      type: 'checkbox',
      header: $fn.value.gettext('Inactive'),
      width: '90px',
      textAlign: 'center',
    },
    {
      name: 'value',
      type: 'number',
      header: $fn.value.gettext('Value'),
      width: '110px',
      validator: ['required', 'numeric'],
    },
    {
      name: 'unit',
      header: $fn.value.gettext('Unit'),
      width: '110px',
      required: true,
    },
    {
      name: 'reading_type',
      header: $fn.value.gettext('Reading Type'),
      width: '120px',
      required: true,
      default: 'reading',
    },
    {
      name: 'reading_date',
      type: 'date',
      header: $fn.value.gettext('Reading Date'),
      required: true,
      width: '120px',
    },
    {
      name: 'reading_time',
      type: 'time',
      header: $fn.value.gettext('Reading Time'),
      required: true,
      width: '120px',
    },
    {
      name: 'notes',
      type: 'textarea',
      header: $fn.value.gettext('Notes'),
      width: '200px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_7315092382398562_paging: b_7315092382398562_paging.value,
    b_7315092382398562_columns: b_7315092382398562_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_meter_reading', {
      parameters: { 'meter-id': props.meter.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_paging', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_columns', nodeId: '7315092382398562' },
    ],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      items,
      ux,
      b_7315092382398562_paging,
      b_7315092382398562_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_7315092382398562_script_u8u506lpm_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('meter_reading').upsert($event.data.row)
    );
  };
  const act_7315092382398562_script_u8u506lpm_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_u8u506lpm_value_callable($event);
      },
    };
  };
  const act_7315092382398562_script_6024epuga_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('meter_reading').delete($event.data.row)
    );
  };
  const act_7315092382398562_script_6024epuga_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_6024epuga_value_callable($event);
      },
    };
  };

  const act_7315092382398562_script_u8u506lpm = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_u8u506lpm_getActionArgs,
  });
  const inst_evh_7315092382398562_save_8gjpancis =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_u8u506lpm,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_save_8gjpancis],
    event: 'save',
  });
  const act_7315092382398562_script_6024epuga = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_6024epuga_getActionArgs,
  });
  const inst_evh_7315092382398562_delete_28kdy0ivp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_6024epuga,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_delete_28kdy0ivp],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_save_8gjpancis,
    inst_evh_7315092382398562_save,
    inst_evh_7315092382398562_delete_28kdy0ivp,
    inst_evh_7315092382398562_delete,
  };

  const evh_7315092382398562_save_8gjpancis = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_save_8gjpancis.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_save = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_delete_28kdy0ivp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_delete_28kdy0ivp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_delete = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpDataTable
    tableId="MeterReadings"
    editable
    :paging="b_7315092382398562_paging"
    :value="
      _.orderBy(
        items,
        ['reading_date', 'reading_time', 'created_at'],
        ['desc', 'desc', 'desc']
      )
    "
    :rowTemplate="
      schemaDefaults('CreateMeterReading', {
        meter,
        meter_id: meter.id,
        unit: meter.unit,
        reading_date: new Date().toLocaleDateString(),
        reading_time: new Date().toLocaleTimeString(),
      })
    "
    prepend-new-row
    :columns="b_7315092382398562_columns"
    @save="evh_7315092382398562_save($event, {})"
    @delete="evh_7315092382398562_delete($event, {})"
  >
  </ItpDataTable>
</template>
